/**
 * ClientProfileForm Component
 *
 * Form for client profile settings
 * Based on Figma design: node-id=127-8767
 */

'use client';

import { useState } from 'react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { AvatarUpload } from './AvatarUpload';
import { SaveButton } from './SaveButton';
import { FormInput } from '../FormInput';
import { LocationPickerModal } from '@/src/features/modals/LocationPickerModal';
import type { SaveStatus } from './hooks/useProfileSave';

const SERVICE_AREA_OPTIONS = [
  '5 km',
  '10 km',
  '15 km',
  '20 km',
  '25 km',
  '30 km',
  '50 km',
  '100 km',
];

export interface ClientProfileFormProps {
  currentAvatarUrl?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  // Location fields
  address: string;
  city: string;
  province: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
  serviceArea: string;
  showLocation: boolean;
  profileDescription: string;
  selectedAvatar: File | null;
  validationErrors: Record<string, string>;
  saveStatus: SaveStatus;
  isSaving: boolean;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onLocationChange: (location: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    latitude: number;
    longitude: number;
  }) => void;
  onLocationSave: (location: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    latitude: number;
    longitude: number;
  }) => Promise<void>;
  onLocationClear: () => void;
  onServiceAreaChange: (value: string) => void;
  onShowLocationChange: (value: boolean) => void;
  onProfileDescriptionChange: (value: string) => void;
  onAvatarChange: (file: File | null) => void;
  onSave: () => void;
  onClearError: (field: string) => void;
}

export function ClientProfileForm({
  currentAvatarUrl,
  firstName,
  lastName,
  email,
  phone,
  address,
  city,
  province,
  postalCode,
  latitude,
  longitude,
  serviceArea,
  showLocation,
  profileDescription,
  selectedAvatar,
  validationErrors,
  saveStatus,
  isSaving,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPhoneChange,
  onLocationChange,
  onLocationSave,
  onLocationClear,
  onServiceAreaChange,
  onShowLocationChange,
  onProfileDescriptionChange,
  onAvatarChange,
  onSave,
  onClearError,
}: ClientProfileFormProps) {
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  // Format location for display
  const formatLocationDisplay = () => {
    const parts: string[] = [];
    if (city) parts.push(city);
    if (postalCode) parts.push(postalCode);

    // If no address details but has coordinates, show coordinates
    if (parts.length === 0 && latitude && longitude) {
      return `📍 Location set (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`;
    }

    return parts.length > 0 ? parts.join(', ') : '';
  };

  const hasLocation = latitude && longitude;

  const handleLocationSelected = async (location: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    latitude: number;
    longitude: number;
  }) => {
    // Update form state
    onLocationChange(location);
    onClearError('location');
    onClearError('city');
    // Save to server immediately
    await onLocationSave(location);
    // Modal will close automatically on success
  };

  return (
    <div className="space-y-8">
      {/* Main Content: Avatar + Form Fields */}
      <div className="flex gap-8">
        {/* Avatar Section - Left Side */}
        <div className="shrink-0">
          <AvatarUpload
            currentAvatarUrl={currentAvatarUrl}
            selectedAvatar={selectedAvatar}
            onAvatarChange={onAvatarChange}
          />
        </div>

        {/* Form Fields - Right Side */}
        <div className="flex-1 space-y-5">
          {/* Name Fields Row */}
          <div className="grid grid-cols-2 gap-5">
            <FormInput
              label="First Name"
              value={firstName}
              onChange={(value) => {
                onFirstNameChange(value);
                onClearError('firstName');
              }}
              error={validationErrors.firstName}
              placeholder="John"
              required
            />
            <FormInput
              label="Last Name"
              value={lastName}
              onChange={(value) => {
                onLastNameChange(value);
                onClearError('lastName');
              }}
              error={validationErrors.lastName}
              placeholder="Smith"
              required
            />
          </div>

          {/* Email + Phone Row */}
          <div className="grid grid-cols-2 gap-5">
            <FormInput
              label="Email"
              type="email"
              value={email}
              onChange={onEmailChange}
              error={validationErrors.email}
              placeholder="email@example.com"
              disabled
            />
            <FormInput
              label="Phone Number (visible only to you)"
              type="tel"
              value={phone}
              onChange={(value) => {
                // Allow international phone numbers in E.164 format
                if (value === '' || value === '+' || /^\+[1-9]\d{0,14}$/.test(value)) {
                  onPhoneChange(value);
                  onClearError('phone');
                }
              }}
              error={validationErrors.phone}
              placeholder="+11234567890"
            />
          </div>

          {/* Location + Service Area Row */}
          <div className="grid grid-cols-2 gap-5">
            {/* Location Field */}
            <div>
              <label className="flex items-center gap-1 mb-2">
                <Typography variant="body" weight="medium">
                  Location
                </Typography>
                <span className="w-2 h-2 rounded-full bg-accent-error" />
              </label>
              <div
                onClick={() => setIsLocationModalOpen(true)}
                className={`w-full px-5 py-3 border rounded-2xl cursor-pointer transition-all hover:border-accent-primary flex items-center justify-between bg-white ${
                  validationErrors.location || validationErrors.city
                    ? 'border-accent-error'
                    : 'border-border-primary'
                }`}
              >
                <Typography
                  variant="body"
                  className={hasLocation ? 'text-text-primary' : 'text-text-tertiary'}
                >
                  {formatLocationDisplay() || 'Select location'}
                </Typography>
                {hasLocation ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onLocationClear();
                    }}
                    className="p-1 hover:bg-background-secondary rounded-full transition-colors"
                  >
                    <Icon name="x" size="sm" className="text-text-secondary" />
                  </button>
                ) : null}
              </div>
              {(validationErrors.location || validationErrors.city) && (
                <Typography variant="bodySm" className="text-accent-error mt-1">
                  {validationErrors.location || validationErrors.city}
                </Typography>
              )}
            </div>

            {/* Service Area Field */}
            <div>
              <label className="flex items-center gap-1 mb-2">
                <Typography variant="body" weight="medium">
                  Service area
                </Typography>
                <span className="w-2 h-2 rounded-full bg-accent-error" />
              </label>
              <div className="relative">
                <select
                  value={serviceArea}
                  onChange={(e) => {
                    onServiceAreaChange(e.target.value);
                    onClearError('serviceArea');
                  }}
                  className={`w-full px-5 py-3 border rounded-2xl appearance-none cursor-pointer bg-white focus:outline-none focus:ring-2 focus:ring-accent-primary ${
                    validationErrors.serviceArea
                      ? 'border-accent-error'
                      : 'border-border-primary'
                  }`}
                >
                  <option value="">Select area</option>
                  {SERVICE_AREA_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none">
                  <Icon name="arrow-down" size="sm" className="text-text-secondary" />
                </div>
              </div>
              {validationErrors.serviceArea && (
                <Typography variant="bodySm" className="text-accent-error mt-1">
                  {validationErrors.serviceArea}
                </Typography>
              )}
            </div>
          </div>

          {/* Show Location Checkbox */}
          <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={showLocation}
                onChange={(e) => onShowLocationChange(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-6 h-6 border-2 border-border-primary rounded bg-white peer-checked:bg-accent-primary peer-checked:border-accent-primary transition-colors flex items-center justify-center">
                {showLocation && (
                  <svg
                    className="w-4 h-4 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            </div>
            <Typography variant="body">Show my location to others</Typography>
          </label>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border-primary border-dashed" />

      {/* Profile Description Section */}
      <div>
        <Typography variant="h3" className="mb-4">
          Profile Description
        </Typography>
        <div className="relative">
          <textarea
            value={profileDescription}
            onChange={(e) => {
              onProfileDescriptionChange(e.target.value.slice(0, 600));
              onClearError('profileDescription');
            }}
            placeholder="Write something important about your services"
            rows={5}
            maxLength={600}
            className={`w-full px-5 py-3 border rounded-2xl focus:outline-none focus:ring-2 bg-white resize-none ${
              validationErrors.profileDescription
                ? 'border-accent-error focus:ring-accent-error'
                : 'border-border-primary focus:ring-accent-primary'
            }`}
          />
        </div>
        <div className="mt-1">
          <Typography variant="bodySm" color="secondary">
            {profileDescription.length}/600
          </Typography>
        </div>
        {validationErrors.profileDescription && (
          <Typography variant="bodySm" className="text-accent-error mt-1">
            {validationErrors.profileDescription}
          </Typography>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-center">
        <button
          onClick={onSave}
          disabled={isSaving}
          className="px-16 py-3 bg-accent-primary text-white rounded-full hover:bg-accent-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]"
        >
          <Typography variant="body" weight="semibold">
            {isSaving ? 'Saving...' : 'Save'}
          </Typography>
        </button>
      </div>

      {/* Status Messages */}
      {saveStatus === 'success' && (
        <div className="flex justify-center">
          <Typography variant="body" className="text-green-600">
            Profile updated successfully!
          </Typography>
        </div>
      )}
      {saveStatus === 'error' && (
        <div className="flex justify-center">
          <Typography variant="body" className="text-red-600">
            Failed to update profile. Please try again.
          </Typography>
        </div>
      )}

      {/* Location Picker Modal */}
      <LocationPickerModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        onLocationSelected={handleLocationSelected}
        initialLocation={{
          address,
          city,
          province,
          postalCode,
          latitude: latitude ?? undefined,
          longitude: longitude ?? undefined,
        }}
        title="Set Your Location"
      />
    </div>
  );
}
